import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { UpgradePanel } from "@/components/UpgradePanel";
import { CoachingStatusCard } from "@/components/CoachingStatusCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const WorkbookButton = ({ available }) => {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const r = await api.get("/program/workbook", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Savior-Life-Discipline-Reset-Workbook.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Couldn't download the workbook right now");
    } finally {
      setLoading(false);
    }
  };
  if (!available) {
    return <div className="text-sm text-[#5C6A61] italic" data-testid="workbook-coming-soon">Your workbook is being finished. It'll appear here soon.</div>;
  }
  return (
    <button onClick={download} disabled={loading} data-testid="download-workbook-btn"
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2A3B32] text-[#FDFBF7] hover:-translate-y-0.5 active:scale-95 transition-transform duration-200 disabled:opacity-60">
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download your workbook (PDF)
    </button>
  );
};

export default function Program() {
  const [program, setProgram] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/program").then((r) => setProgram(r.data)).catch(() => {});
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const toggle = async (id, done) => {
    try {
      const r = await api.post("/program/progress", { exercise_id: id, done });
      setProgram((p) => ({ ...p, completed: r.data.completed }));
    } catch {
      toast.error("Couldn't save your progress");
    }
  };

  if (!program) return <div className="min-h-screen bg-[#FDFBF7]"><AppHeader /></div>;

  if (!program.owned) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] grain">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-6 py-12"><UpgradePanel /></main>
      </div>
    );
  }

  const total = program.weeks.reduce((n, w) => n + w.exercises.length, 0);
  const done = program.completed.length;

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="eyebrow text-[#E07A5F]">your program</div>
            <h1 className="font-serif-display font-light text-4xl sm:text-5xl text-[#1C221D] mt-2">{program.name}</h1>
            <p className="text-[#5C6A61] mt-3 leading-relaxed">{program.description}</p>
          </div>
          {program.weeks.map((w) => (
            <section key={w.week} className="bg-white rounded-3xl border border-[#2A3B32]/10 p-7" data-testid={`week-${w.week}`}>
              <div className="eyebrow text-[#5C6A61]">week {w.week}</div>
              <h2 className="font-serif-display text-3xl text-[#1C221D]">{w.title}</h2>
              <p className="text-sm text-[#5C6A61] mt-1 italic">{w.theme}</p>
              <ul className="mt-5 space-y-4">
                {w.exercises.map((e) => {
                  const checked = program.completed.includes(e.id);
                  return (
                    <li key={e.id} className="flex gap-4">
                      <Checkbox checked={checked} onCheckedChange={(v) => toggle(e.id, !!v)} data-testid={`exercise-${e.id}`} className="mt-1" />
                      <div>
                        <div className={`font-medium ${checked ? "line-through text-[#5C6A61]" : "text-[#1C221D]"}`}>{e.title}</div>
                        <p className="text-sm text-[#5C6A61] leading-relaxed mt-1">{e.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <CoachingStatusCard status={status} />
          <div className="bg-white rounded-3xl p-6 border border-[#2A3B32]/10" data-testid="program-progress">
            <div className="eyebrow text-[#5C6A61]">progress</div>
            <div className="font-serif-display text-4xl text-[#1C221D] mt-2">{done}<span className="text-lg text-[#5C6A61]">/{total} exercises</span></div>
            <div className="mt-3 h-2 rounded-full bg-[#F2EBE5] overflow-hidden">
              <div className="h-full bg-[#81B29A] transition-[width] duration-500" style={{ width: `${(done / total) * 100}%` }} />
            </div>
          </div>
          <div className="bg-[#F2EBE5] rounded-3xl p-6 space-y-3">
            <div className="eyebrow text-[#5C6A61]">workbook</div>
            <WorkbookButton available={program.workbook_available} />
          </div>
        </aside>
      </main>
    </div>
  );
}
